import React, { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import OrderList from "../../container/OrderManagementFlow/OrderList";
import { LanguageContext } from "../../../context/LanguageContext";
import useGetUpdateOrder from "../../../hooks/useGetUpdateOrder";
import useOrderLines from "../../../hooks/useOrderLines";
import BusinessLoader from "../../common/BusinessLoader";
import fourOFour from "../../../assets/images/404.webp";
import { setCurrentScreen } from "firebase/analytics";
import { analytics } from "../../../config/firebase";
import {
  AppNonIdeal,
  InnerWrapper,
  MainContainer,
  TitleAndActionBox,
} from "../../common";
import { Grid, CircularProgress } from "@mui/material";
import ChatwootWidget from "../../common/Chatwoot";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../../context/AuthContext";
import NonIdealNew from "../../common/NonIdealNew";
import { useFiltersHandler } from "../../../hooks/useFilterHandler";
import moment from "moment";

const OrderManagement = () => {
  const { user } = useAuthContext();
  let isSeller = localStorage.getItem("is_marketplace_seller");
  const navigate = useNavigate();
  const {
    handleReturnOrderList,
    handleOrderList,
    handleOrderStatusList,
    orderListParams,
    returnListParams,
    returnList,
    orderList,
    loading,
    setLoading
  } = useOrderLines() || {};
  const { handleOrder, handleBulkOrder, setOrder, order } =
    useGetUpdateOrder() || {};
  const { ordersLoad, returnLoad, pageLoad, searchLoad } = loading || [];
  const { rejectReasons } = orderListParams || {};
  const { order_detail } = order || [];

  const [I18n] = useContext(LanguageContext);
  const [updateLoad, setUpdateLoad] = useState(false);
  const [getLoad, setGetLoad] = useState(false);
  const [markOptions, setMarkOptions] = useState([
    { name: I18n["accepted"], statusValue: "accepted" },
    // { name: I18n["ready_to_ship"], statusValue: "ready_to_ship" },
  ]);
  const [selectedStatus, setSelectedStatus] = useState(undefined);
  const [isChatWidgetOpen, setIsChatWidgetOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const statusCancelSourceRef = useRef(null);
  const prevStatusRef = useRef(undefined);
  const searchDebounceRef = useRef(null);
  const prevFiltersRef = useRef(null);

  const {
    filters,
    appliedChips,
    handleFilterChange,
    resetFilters,
    handleRemoveChip,
    isResetFilter,
    setIsResetFilter,
    isAppliedFilter
  } = useFiltersHandler(undefined, I18n);

  const isOrderList = filters.view !== "returns";
  useEffect(() => {
    setCurrentScreen(analytics, "OrderManagement_screen");
  }, []);

  const date = filters?.date;
  // Sent to the API, so always English month names: the display formatter follows the
  // app language (e.g. "05 اگست 2026" in Urdu), which the server can't parse
  const formatApiDate = (value) =>
    value ? moment(value).locale("en").format("DD MMM YYYY") : "";
  let formattedDateFilter = {
    start_date: formatApiDate(date?.startDate),
    end_date: formatApiDate(date?.endDate),
  };
  const fetchOrders = () => {
    let cancelSource;
    const statusChanged = filters.status !== prevStatusRef.current;
    prevStatusRef.current = filters.status;

    if (statusChanged) {
      if (statusCancelSourceRef.current) statusCancelSourceRef.current.cancel();
      statusCancelSourceRef.current = axios.CancelToken.source();
      cancelSource = statusCancelSourceRef.current;
    }
    setOffset(0)
    setLoading((e) => ({ ...e, searchLoad: true }))
    if (filters.view !== "returns") handleOrderList(0, 15, filters.keywords, { ...formattedDateFilter, order_status: filters?.status || undefined }, cancelSource);
    else if (filters.view === "returns") handleReturnOrderList(0, 15, filters.keywords, { ...formattedDateFilter, order_status: filters?.status || undefined }, cancelSource);
  };

  useEffect(() => {
    if (!isSeller) return;

    const prevFilters = prevFiltersRef.current;
    // This whole screen is API-backed (GET_SELLER_ORDERS), so only the search box needs debouncing.
    const isKeywordsOnlyChange =
      prevFilters &&
      prevFilters.keywords !== filters.keywords &&
      Object.keys(filters).every(
        (key) => key === "keywords" || filters[key] === prevFilters[key],
      );
    prevFiltersRef.current = filters;

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    if (isKeywordsOnlyChange) {
      searchDebounceRef.current = setTimeout(fetchOrders, 500);
    } else {
      fetchOrders();
    }

    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [filters]);

  const toggleChatWidget = () => {
    setIsChatWidgetOpen(!isChatWidgetOpen);
  };

  // /**
  //  * apply order filters
  //  * @returns search list
  //  */
  // const applyResetFilter = (keyword, filters, apply) => {
  //   if (apply) handleOrderList(0, 15, keyword, filters);
  //   else handleOrderList();
  // };

  // /**
  //  * Get search result by keywork as params
  //  * @returns search list
  //  */
  // const onSearch = async (keyword) => {
  //   triggerLogEvent(analytics, "sc_orders_search", { keyword });
  //   if (!keyword) {
  //     handleOrderList(0, 15, keyword);
  //     handleReturnOrderList(0, 15, keyword);
  //   } else {
  //     isOrderList
  //       ? handleOrderList(0, 15, keyword)
  //       : handleReturnOrderList(0, 15, keyword);
  //   }
  // };



  /**
   * Receive endpoint and params to get order
   * @returns order;
   */
  const handleGetOrder = async (endpoint, params) => {
    navigate(
      isOrderList
        ? `/SaddarOrders/${params.id}`
        : `/SaddarOrders/returns/${params.id}`
    );
  };

  /**
   * Receive endpoint, params for for accept, reject, shipped, dispatch date and edit order.
   * Referesh param for updating the detail view of order
   * @returns perform action and return updtaed list
   */
  const handleOrderStatus = (endpoint, params, refresh, multiple = false) => {
    setUpdateLoad(true);
    setGetLoad(true);
    return new Promise(async (resolve, reject) => {
      try {
        multiple
        ? await handleBulkOrder(endpoint, params, refresh)
        : await handleOrder(endpoint, params, refresh);
        isOrderList ? await handleOrderList() : handleReturnOrderList();
        resetFilters()
        resolve();
        setUpdateLoad(false);
        setGetLoad(false);
      } catch (err) {
        reject(err);
        setUpdateLoad(false);
        setGetLoad(false);
      }
    });
  };

  return isSeller ? (
    <MainContainer fluid className={"quickSalePage"} >
      <Grid container>
        <Grid item md={12}>
          <TitleAndActionBox
            data={{
              title: "saddar_orders",
              isBack: true,
            }}
          />
          <InnerWrapper className="innerWrapper newInnerWrapper orderManagementWrapper">
            {ordersLoad ? (
              <div className="orderLoaderMain">
                <CircularProgress size={24} className="loaderCircle" />
              </div>
            ) : orderList || returnList ? (
              <>
                <OrderList
                  data={{
                    handlePagination: (offset, limit, search, filters) => {
                      isOrderList ? handleOrderList(offset, limit, search, filters) : handleReturnOrderList(offset, limit, search, filters)
                    },
                    handleOrderStatus,
                    handleGetOrder,
                    returnLoad,
                    returnListParams,
                    returnList,
                    updateLoad,
                    orderList,
                    orderListParams,
                    pageLoad,
                    selectedStatus,
                    setSelectedStatus,
                    markOptions,
                    searchLoad,
                    toggleChatWidget,
                    handleOrderStatusList,
                    I18n,
                    isOrderList,
                    filters,
                    appliedChips,
                    handleFilterChange,
                    resetFilters,
                    handleRemoveChip,
                    isResetFilter,
                    setIsResetFilter,
                    offset,
                    setOffset,
                    formattedDateFilter,
                    isAppliedFilter
                  }}
                />
              </>
            ) : (
              <NonIdealNew
                subtitle={I18n.You_havent_placed_any_order_yet}
                title={I18n.saddar_orders}
                loading={false}
              />
            )}
            <BusinessLoader loadBusiness={getLoad} />

            {isChatWidgetOpen ? <ChatwootWidget user={user} /> : null}
          </InnerWrapper>
        </Grid>
      </Grid>
    </MainContainer>
  ) : (
    <MainContainer fluid>
      <Grid container>
        <Grid item md={12}>
          <TitleAndActionBox
            data={{
              title: "saddar_orders",
              isBack: true,
            }}
          />
          <InnerWrapper className="innerWrapper newInnerWrapper">
            <AppNonIdeal
              data={{
                title: I18n.page_not_found,
                subtitle: I18n.we_re_sorry_the_page_you_requested,
                image: fourOFour,
                I18n,
              }}
            />
          </InnerWrapper>
        </Grid>
      </Grid>
    </MainContainer>
  );
};

export default OrderManagement;
